import { useCallback, useEffect } from 'react';

import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Accordion from '@/components/atoms/Accordion/Accordion';
import AvatarUpload from '@/components/atoms/Avatar/AvatarUpload/AvatarUpload';
import TitleSubtitle from '@/components/atoms/TitleSubtitle/TitleSubtitle';
import { Input } from '@/components/ui/input';
import { useIntegrations } from '@/hooks/useIntegrations';
import usePrompt from '@/hooks/usePrompt';
import { cn } from '@/lib/utils';
import { setDirty, setExpanded } from '@/redux/integrations/actions';
import { selectAccordion } from '@/redux/integrations/selectors';
import { getPermissions } from '@/redux/permissions/selectors';
import { AccordionFieldsetProps, ViberIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { errorMessage, trimOnBlur } from '@/util/validation/helpers';
import { integrationRulesViber } from '@/util/validation/rules/integration-rules';

import styles from './ViberChatAppearance.module.scss';

type Form = { welcome_trigger_message: string; avatar_url: string };

const ViberChatAppearance = ({
  type,
  integration,
  toggleAccordion,
  registerAccordion,
}: AccordionFieldsetProps<Form, ViberIntegration>) => {
  const { t } = useTranslation();
  const { expanded } = useSelector(selectAccordion);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const deskId = integration?.desk_id;
  const integrationId = integration?.integration_id;

  const { updateIntegration, updateStatus } = useIntegrations<ViberIntegration>(
    deskId,
    integrationId
  );
  const dispatch = useDispatch();

  const formMethods = useForm<Form>({ mode: 'onSubmit' });

  const {
    reset,
    formState: { errors, isDirty },
    handleSubmit,
    getValues,
    trigger,
    setValue,
    control,
  } = formMethods;

  const avatarUrl = useWatch({ name: 'avatar_url', control });

  const welcomeTriggerMessageErrorMessage = errorMessage({
    field: errors.welcome_trigger_message,
    maxLength: integrationRulesViber.welcomeTriggerMessage.maxLength,
  });

  useEffect(() => {
    reset({
      welcome_trigger_message: integration?.config?.welcome_trigger_message || '',
      avatar_url: integration?.config?.avatar || '',
    });
  }, [integration?.config?.avatar, integration?.config?.welcome_trigger_message, reset]);

  const handleUpdateIntegration = useCallback(() => {
    const { welcome_trigger_message, avatar_url } = getValues();
    updateIntegration(
      {
        ...integration,
        config: {
          ...integration.config,
          welcome_trigger_message: welcome_trigger_message || undefined,
          avatar: avatar_url || undefined,
        },
      },
      {
        onSuccess: () => {
          if (expanded === type) {
            dispatch(setExpanded(false));
          }
        },
      }
    );
  }, [dispatch, expanded, getValues, integration, type, updateIntegration]);

  usePrompt(isDirty && canWrite, undefined, undefined, handleSubmit(handleUpdateIntegration));

  useEffect(() => {
    dispatch(setDirty(isDirty));
  }, [dispatch, isDirty]);

  // for reseting changes
  useEffect(() => {
    if (expanded !== type && isDirty) {
      reset();
    }
  }, [expanded, isDirty, reset, type]);

  const handleAvatarUpdate = useCallback<(data: { avatar: string }) => void>(
    (data) => {
      setValue('avatar_url', data?.avatar, { shouldDirty: true });
    },
    [setValue]
  );

  return (
    <>
      <Accordion
        title={t('integrations.viber_chat_appearance.title')}
        subtitle={t('integrations.viber_chat_appearance.subtitle')}
        disabled={!isDirty}
        onSubmit={handleSubmit(handleUpdateIntegration)}
        isLoading={updateStatus === 'pending'}
        expanded={expanded === type}
        handleChange={toggleAccordion(type)}
        readOnly={!canWrite}
        ref={registerAccordion(type, handleSubmit(handleUpdateIntegration), trigger)}
      >
        <div className={styles.wrapper}>
          <TitleSubtitle
            title={t('integrations.viber_chat_appearance.welcome_trigger_message.title')}
            subtitle={t('integrations.viber_chat_appearance.welcome_trigger_message.subtitle')}
            error={!!errors.welcome_trigger_message}
          />
          <div className={styles.textArea}>
            <Controller
              control={control}
              name="welcome_trigger_message"
              rules={{ maxLength: { value: 64, message: '' } }}
              render={({ field }) => (
                <Input
                  id="welcome_trigger_message"
                  placeholder={t(
                    'integrations.viber_chat_appearance.welcome_trigger_message.placeholder'
                  )}
                  aria-invalid={!!errors.welcome_trigger_message}
                  readOnly={!canWrite}
                  {...field}
                  value={field.value ?? ''}
                  onBlur={trimOnBlur(field)}
                />
              )}
            />
            {welcomeTriggerMessageErrorMessage && (
              <p className="mt-1 text-sm text-warning">{welcomeTriggerMessageErrorMessage}</p>
            )}
          </div>
        </div>
        <div className={cn(styles.wrapper, { [styles.disabled]: !canWrite })}>
          <div className="pb-3 text-xs leading-4 font-semibold text-foreground-muted uppercase">
            {t('integrations.viber_chat_appearance.avatar.title')}
          </div>
          <AvatarUpload onUpdate={handleAvatarUpdate} label={'userAvatar'} avatar={avatarUrl} />
        </div>
      </Accordion>
    </>
  );
};

export default ViberChatAppearance;
